/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { connect } from "react-redux";
import { set_value_layer } from "../../App/actions/layerActions";

// --- PURE HELPER FUNCTIONS (Optimized for Canvas) ---

const drawArrowHead = (ctx, tipx, tipy, ux, uy, hpx, hpy, type) => {
  const hl = 8;
  const hw = 6;
  const basex = tipx - ux * hl;
  const basey = tipy - uy * hl;

  ctx.beginPath();
  if (type === "circle") {
    ctx.arc(tipx, tipy, hw / 2, 0, Math.PI * 2);
  } else {
    // Triangle
    ctx.moveTo(tipx, tipy);
    ctx.lineTo(basex + hpx * (hw / 2), basey + hpy * (hw / 2));
    ctx.lineTo(basex - hpx * (hw / 2), basey - hpy * (hw / 2));
  }
  ctx.closePath();
  ctx.fill();
};

const drawArrowLine = (ctx, sp_x, sp_y, ep_x, ep_y, hw, hh, style) => {
  const b_left = ep_x - hw;
  const b_right = ep_x + hw;
  const b_top = ep_y - hh;
  const b_bottom = ep_y + hh;

  // L-Shape Elbow Logic
  const mid_x = ep_x;
  const mid_y = sp_y;

  let final_x = ep_x;
  let final_y = ep_y;

  const dx = ep_x - mid_x;
  const dy = ep_y - mid_y;

  if (Math.abs(dx) > Math.abs(dy)) final_x = dx > 0 ? b_left : b_right;
  else final_y = dy > 0 ? b_top : b_bottom;

  // Draw Line
  ctx.beginPath();
  ctx.moveTo(sp_x, sp_y);

  // Only draw elbow if points aren't aligned (optimization)
  if (Math.abs(mid_x - sp_x) > 1 || Math.abs(mid_y - sp_y) > 1) {
    ctx.lineTo(mid_x, mid_y);
  }
  ctx.lineTo(final_x, final_y);
  ctx.stroke();

  // Draw Arrow Head
  let start_dx = mid_x - sp_x;
  let start_dy = mid_y - sp_y;

  if (Math.abs(start_dx) < 1 && Math.abs(start_dy) < 1) {
    start_dx = final_x - sp_x;
    start_dy = final_y - sp_y;
  }

  const len = Math.hypot(start_dx, start_dy) || 1;
  drawArrowHead(
    ctx,
    sp_x,
    sp_y,
    -(start_dx / len),
    -(start_dy / len),
    start_dy / len,
    -(start_dx / len),
    style,
  );
};

// --- COMPONENT ---

const ANNOTATION_COLLECTION = ({ map_object, data, set_value_layer }) => {
  const canvasRef = useRef(null);
  const [containerElement, setContainerElement] = useState(null);
  const htmlRefs = useRef({});

  // Ticking flag for RAF loop to prevent stacking
  const isTicking = useRef(false);

  // Refs for access inside the loop without dependency issues
  const dataRef = useRef(data);
  const mapRef = useRef(map_object);

  useEffect(() => {
    dataRef.current = data;
  }, [data]);
  useEffect(() => {
    mapRef.current = map_object;
  }, [map_object]);

  // --- 1. SETUP DOM ELEMENTS ---
  useEffect(() => {
    if (!map_object) return;

    const canvas = document.createElement("canvas");
    Object.assign(canvas.style, {
      position: "absolute",
      top: "0",
      left: "0",
      pointerEvents: "none",
      zIndex: 1,
    });

    const container = document.createElement("div");
    Object.assign(container.style, {
      position: "absolute",
      top: "0",
      left: "0",
      width: "100%",
      height: "100%",
      pointerEvents: "none",
      zIndex: 2,
    });

    const mapCanvasContainer = map_object.getCanvasContainer();
    mapCanvasContainer.appendChild(canvas);
    mapCanvasContainer.appendChild(container);

    canvasRef.current = canvas;
    setContainerElement(container);

    return () => {
      if (canvas.parentNode) canvas.parentNode.removeChild(canvas);
      if (container.parentNode) container.parentNode.removeChild(container);
    };
  }, [map_object]);

  // --- 2. THE OPTIMIZED RENDER LOOP ---
  useEffect(() => {
    if (!map_object || !containerElement) return;

    const update = () => {
      // RESET TICK
      isTicking.current = false;

      const map = mapRef.current;
      const _data = dataRef.current;
      const canvas = canvasRef.current;
      if (!map || !canvas || !_data) return;

      // --- PHASE 1: CANVAS SETUP ---
      const dpr = window.devicePixelRatio || 1;
      const rect = map.getCanvas().getBoundingClientRect();
      const width = rect.width;
      const height = rect.height;

      // Resize only if strictly necessary (expensive operation)
      const targetW = Math.round(width * dpr);
      const targetH = Math.round(height * dpr);
      if (canvas.width !== targetW || canvas.height !== targetH) {
        canvas.width = targetW;
        canvas.height = targetH;
        canvas.style.width = `${width}px`;
        canvas.style.height = `${height}px`;
      }

      const ctx = canvas.getContext("2d");
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.scale(dpr, dpr);

      // Shared Styles for Canvas
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.strokeStyle = "#111";
      ctx.fillStyle = "#111";
      ctx.lineWidth = 2;

      // --- PHASE 2: BATCH CALCULATION (READS) ---
      // We calculate everything first to avoid forcing reflows later
      const calculatedItems = [];
      const len = _data.length;

      for (let i = 0; i < len; i++) {
        const item = _data[i];

        // Project Anchor
        const anchor = map.project([item.anchor_long, item.anchor_lat]);
        const sp_x = anchor.x;
        const sp_y = anchor.y;

        // Project Reference Point for Scale
        // Optimization: We manually calculate pixel delta for speed instead of full projection if possible,
        // but map.project is safest for all projections (Globe/Mercator)
        const ref_p = map.project([
          item.anchor_long + item.width,
          item.anchor_lat,
        ]);
        const box_px_w = Math.hypot(ref_p.x - sp_x, ref_p.y - sp_y);

        // Visibility Culling
        if (box_px_w < 20) {
          calculatedItems.push({ id: item.id, visible: false });
          continue;
        }

        const ratio = item.height / item.width;
        const box_px_h = box_px_w * ratio;

        const arrow_px_w = box_px_w * (item.arrow_width / item.width);
        const arrow_px_h = box_px_w * (item.arrow_height / item.width);
        const hw = box_px_w / 2;
        const hh = box_px_h / 2;

        let ep_x = sp_x;
        let ep_y = sp_y;

        // Position Logic
        switch (item.box_position) {
          case "top":
            ep_y = sp_y - (arrow_px_h + hh);
            break;
          case "bottom":
            ep_y = sp_y + (arrow_px_h + hh);
            break;
          case "right":
            ep_x = sp_x + (arrow_px_w + hw);
            break;
          case "left":
            ep_x = sp_x - (arrow_px_w + hw);
            break;
          case "top_right":
            ep_y = sp_y - (arrow_px_h + hh);
            ep_x = sp_x + (arrow_px_w + hw);
            break;
          case "top_left":
            ep_y = sp_y - (arrow_px_h + hh);
            ep_x = sp_x - (arrow_px_w + hw);
            break;
          case "bottom_right":
            ep_y = sp_y + (arrow_px_h + hh);
            ep_x = sp_x + (arrow_px_w + hw);
            break;
          case "bottom_left":
            ep_y = sp_y + (arrow_px_h + hh);
            ep_x = sp_x - (arrow_px_w + hw);
            break;
          default:
            break;
        }

        calculatedItems.push({
          id: item.id,
          visible: true,
          sp_x,
          sp_y,
          ep_x,
          ep_y,
          box_px_w,
          box_px_h,
          hw,
          hh,
          arrow_style: item.arrow_style,
        });
      }

      // --- PHASE 3: BATCH DRAWING (CANVAS) ---
      for (let i = 0; i < len; i++) {
        const calc = calculatedItems[i];
        if (calc.visible) {
          drawArrowLine(
            ctx,
            calc.sp_x,
            calc.sp_y,
            calc.ep_x,
            calc.ep_y,
            calc.hw,
            calc.hh,
            calc.arrow_style,
          );
        }
      }

      // --- PHASE 4: BATCH DOM UPDATES (WRITES) ---
      for (let i = 0; i < len; i++) {
        const calc = calculatedItems[i];
        const el = htmlRefs.current[calc.id];

        if (!el) continue;

        if (!calc.visible) {
          if (el.style.display !== "none") el.style.display = "none";
          continue;
        }

        // Apply visual updates
        if (el.style.display === "none") el.style.display = "block";

        // Using translate3d for GPU acceleration
        // Using toFixed(1) reduces style recalculation churn on sub-pixels
        el.style.transform = `translate3d(${calc.ep_x - calc.hw}px, ${calc.ep_y - calc.hh}px, 0)`;
        el.style.width = `${calc.box_px_w}px`;
        el.style.height = `${calc.box_px_h}px`;

        // CSS Variables for internal scaling
        el.style.setProperty("--fs-title", `${calc.box_px_w * 0.08}px`);
        el.style.setProperty("--fs-desc", `${calc.box_px_w * 0.055}px`);
        el.style.setProperty("--padding", `${calc.box_px_w * 0.05}px`);
        el.style.setProperty("--radius", `${calc.box_px_w * 0.07}px`);
      }
    };

    // --- OPTIMIZED EVENT LISTENER (RAF LOOP) ---
    // Instead of forcing a draw on every event, we just flag that we need a frame.
    // This prevents the "stacking" of draw calls if events fire faster than the screen.
    const onMapChange = () => {
      if (!isTicking.current) {
        requestAnimationFrame(update);
        isTicking.current = true;
      }
    };

    map_object.on("move", onMapChange);
    map_object.on("resize", onMapChange);

    // Initial Draw
    onMapChange();

    return () => {
      map_object.off("move", onMapChange);
      map_object.off("resize", onMapChange);
      isTicking.current = false; // Reset tick on cleanup
    };
  }, [map_object, containerElement]);

  // Force update when DATA changes (outside the event loop)
  useEffect(() => {
    if (!map_object) return;
    // Trigger the loop manually once
    requestAnimationFrame(() => {
      // We can't access 'update' here easily due to scope,
      // but firing a minimal map move is a hacky way.
      // Better: Just relying on the next map interaction or
      // creating a separate ref for the update function if strictly needed.
      // Since data changes usually come with map interactions or are rare,
      // we can trigger a map repaint which might trigger listeners if configured,
      // or just accept that the next frame interaction will update it.
      // To be safe:
      map_object.fire("move");
    });
  }, [data, map_object]);

  if (!containerElement) return null;

  return createPortal(
    <>
      {data.map((item) => (
        <div
          key={item.id}
          ref={(el) => (htmlRefs.current[item.id] = el)}
          // Base Styles
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "0px",
            height: "0px",
            backgroundColor: "white",
            boxShadow: "0 2px 5px rgba(0,0,0,0.3)",
            overflow: "hidden",
            display: "none", // Hidden by default
            pointerEvents: "auto",
            boxSizing: "border-box",
            borderRadius: "var(--radius)",
            padding: "var(--padding)",
            // Optimization Hint: helps browser separate this into a composite layer
            willChange: "transform, width, height",
            // Hardware acceleration hint
            transform: "translate3d(0,0,0)",
          }}
          onClick={(e) => {
            e.stopPropagation();
            set_value_layer({
              key: "modal_annotation_data",
              value: item,
            });
          }}
        >
          {item.image_1 && (
            <div
              style={{
                width: "100%",
                height: "55%",
                backgroundImage: `url(${item.image_1})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                borderRadius: "calc(var(--radius) / 2)",
                marginBottom: "var(--padding)",
              }}
            />
          )}
          <div
            style={{
              fontSize: "var(--fs-title)",
              fontWeight: "bold",
              lineHeight: 1.1,
              marginBottom: "calc(var(--padding) / 2)",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {item.title}
          </div>
          <div
            style={{
              fontSize: "var(--fs-desc)",
              lineHeight: 1.2,
              color: "#444",
              overflow: "hidden",
            }}
          >
            {item.desc}
          </div>
        </div>
      ))}
    </>,
    containerElement,
  );
};

export default connect(null, { set_value_layer })(ANNOTATION_COLLECTION);
